/**
 * "Count and Play" — counting 1-10 with the drum; the narrator counts each
 * beat (one-to-one correspondence).
 */

import { createSignal, For, onCleanup, onMount, Show } from "solid-js";
import {
  createCountAndPlayGame,
  createProgressStore,
  gameStickerId,
  numberKey,
} from "@melodimals/game-core";
import { Star } from "@fleet/icons";
import { useAudio } from "@/providers/audio-provider";
import { SoundLoadError } from "@/features/instruments/sound-load-error";
import { RewardPopup } from "@/features/rewards/reward-popup";
import { useRewards } from "@/features/rewards/use-rewards";
import { useAppConfig } from "@/providers/config-provider";

const CELEBRATE_MS = 1800;

export function CountAndPlayGame() {
  const audio = useAudio();
  const progress = createProgressStore();
  const rewards = useRewards();
  // How far to count and how many rounds: configuration, not a constant.
  const { games } = useAppConfig().config();
  const game = createCountAndPlayGame({ max: games.countMax, rounds: games.countRounds });

  const [target, setTarget] = createSignal(game.state().target);
  const [count, setCount] = createSignal(0);
  const [celebrating, setCelebrating] = createSignal(false);
  const [finished, setFinished] = createSignal(false);
  const [ready, setReady] = createSignal(false);
  // P23/I3: preloading can FAIL (dropped samples). Before, the rejection was
  // lost and `ready` stayed false forever: the drum showed up
  // disabled with no explanation at all. Now the failure is a state.
  const [soundFailed, setSoundFailed] = createSignal(false);
  const timers: ReturnType<typeof setTimeout>[] = [];
  onCleanup(() => {
    for (const t of timers) {
      clearTimeout(t);
    }
  });

  const preloadSounds = () => {
    setSoundFailed(false);
    void (async () => {
      try {
        await audio.loadInstrument("drums");
        setReady(true);
      } catch {
        setSoundFailed(true);
      }
    })();
  };

  onMount(preloadSounds);

  const sayCount = (n: number) => {
    const key = numberKey(n);
    if (key) {
      void audio.narrator.say(key);
    }
  };

  const hit = () => {
    if (!ready()) {
      return;
    }
    audio.playNote("drums", "kick");
    const result = game.hit();
    if (result === "ignored") {
      return;
    }
    setCount(game.state().count);
    sayCount(game.state().count);

    if (result === "round-complete") {
      setCelebrating(true);
      progress.recordLevel("count-and-play", game.state().round);
      timers.push(
        setTimeout(() => {
          void audio.narrator.say("praise.great");
        }, 900),
        setTimeout(() => {
          game.nextRound();
          setTarget(game.state().target);
          setCount(0);
          setCelebrating(false);
        }, CELEBRATE_MS),
      );
    } else if (result === "finished") {
      setFinished(true);
      progress.recordLevel("count-and-play", games.countRounds);
      rewards.award(gameStickerId("count-and-play"));
      timers.push(
        setTimeout(() => {
          void audio.narrator.say("praise.wow");
        }, 900),
      );
    }
  };

  return (
    <div class="flex h-full flex-col items-center justify-center gap-[clamp(1rem,4vh,2rem)]">
      {/* P23/I3: el error ENVUELVE al contenido, no se encadena. Encadenarlo
          dejaría el juego mudo pintado debajo del aviso. */}
      <Show when={soundFailed()}>
        <SoundLoadError testId="count-and-play" onRetry={preloadSounds} />
      </Show>
      <Show when={!soundFailed()}>
        <Show
          when={!finished()}
          fallback={
            <div class="flex items-center gap-2" aria-hidden="true">
              <Star class="h-10 w-10 fill-amber-400 text-amber-500" />
              <Star class="h-14 w-14 fill-amber-400 text-amber-500" />
              <Star class="h-10 w-10 fill-amber-400 text-amber-500" />
            </div>
          }
        >
          {/* N estrellas: las contadas se encienden */}
          <div class="flex max-w-md flex-wrap items-center justify-center gap-2" aria-hidden="true">
            <For each={Array.from({ length: target() })}>
              {(_, i) => (
                <Star
                  class={`h-[clamp(2rem,7vmin,3rem)] w-[clamp(2rem,7vmin,3rem)] transition-transform ${
                    i() < count()
                      ? "fill-amber-400 text-amber-500 scale-110"
                      : "fill-white/40 text-white"
                  } ${celebrating() ? "animate-bounce" : ""}`}
                />
              )}
            </For>
          </div>

          <button
            type="button"
            onPointerDown={hit}
            disabled={!ready() || celebrating()}
            class="flex aspect-square w-[clamp(8rem,38vmin,12rem)] items-center justify-center rounded-full border-8 border-white/70 bg-rose-400 text-[clamp(3.5rem,18vmin,6rem)] shadow-xl transition-transform active:scale-90 disabled:opacity-60"
            aria-label="drum"
          >
            <span aria-hidden="true">🥁</span>
          </button>
        </Show>
      </Show>

      <RewardPopup stickerId={rewards.pending()} onDismiss={rewards.dismiss} />
    </div>
  );
}
